<script setup>
import { XMarkIcon } from '@heroicons/vue/24/outline'

const tech = useTech()
const now = new Date()
const config = useRuntimeConfig()
const route = useRoute()

const bannerDismissed = useCookie('is-bb-banner-dismissed')

const { data } = await useFetch(config.public.BANNER_JSON_URL, {
  query: { affiliate: 'certificates' },
  getCachedData (key) {
    return useNuxtData(key).data.value
  },
  timeout: 5000,
  onResponseError () {
    return { data: [] }
  }
})

const phase = data.value
  ?.flat()
  .map((item) => ({
    ...item,
    dateString: item.ends,
    remaining: new Date(item.ends) - now,
    header: stylize(item.header),
    title: stylize(item.title),
    prelude: stylize(item.prelude),
    subtitle: stylize(item.subtitle),
    appTitle: stylize(item.appTitle)
  }))
  .find((item) => item.remaining > 0)

function stylize (string) {
  return string
    ?.replace(
      '[',
      '<span class="bg-gradient-to-r from-brilliant-rose font-bold to-dodger-blue bg-clip-text text-transparent">'
    )
    .replace(']', '</span>')
}

const user = useUser()
const { list: certifications } = useCertifications()
const hasPurchased = certifications?.some(certification => user.accessTo(certification.id, 'EXAM'))

const hasPurchasedExamOnly = computed(() => {
  return !certifications?.some(certification => user.accessTo(certification.id, 'TRAINING'))
})

const isBannerClosed = ref(true)

const closeBanner = () => {
  isBannerClosed.value = true
  localStorage.setItem(`banner_${phase?.id}`, false)
}

onMounted(() => {
  if (window) {
    const banner = localStorage?.getItem(`banner_${phase?.id}`)
    isBannerClosed.value = banner !== null
  }
})

const showBannerOnLanding = computed(() => {
  if (route.path.includes('/vuejs') && !route?.path?.includes('/blog')) {
    return true
  } else {
    return phase.showOnHomepage || false
  }
})

const currentTheme = computed(() => {
  return phase?.theme || 1
})

const allowBannerOnPage = inject('allowBanners', true)

const { AnalyticsEvent, track } = useAnalytics()
function trackBannerClick () {
  if (!phase?.link) {
    track(AnalyticsEvent.VIEW_PRICING, {
      tech: tech.id,
      via: 'banner'
    })
  }
}
</script>

<template>
  <BannerRepublic
    v-if="!user?.isGuest && hasPurchased && tech.id === 'vuejs' && !bannerDismissed && allowBannerOnPage"
    dismissible
    @dismiss="bannerDismissed = true" />
  <div
    v-if="!isBannerClosed && (user?.isGuest || !hasPurchased || hasPurchasedExamOnly) && phase && showBannerOnLanding && allowBannerOnPage"
    class="!dark relative">
    <NuxtLink
      :to="phase?.link || '/vuejs#pricing'"
      :target="tech.id === 'vuejs' ? '_self' : '_blank'"
      class="dark group relative flex h-14 w-full items-center  justify-center gap-3 px-2 md:h-[72px] md:gap-5 md:px-4 lg:h-14 xl:gap-10"
      :class="{ '!h-16': currentTheme === 2 || currentTheme === 3, '!bg-[linear-gradient(90deg, rgba(66, 85, 255, 0.20)0%, rgba(0, 163, 255, 0.20)100%), #07080E;]' : route.path === '/' }"
      :style="{
        background: phase?.darkMode
          ? '#1b4439'
          : currentTheme === 3 && route.path === '/' ? 'linear-gradient(90deg, rgba(66, 85, 255, 0.20)0%, rgba(0, 163, 255, 0.20)100%)':'linear-gradient(107deg, rgba(248, 100, 189, 0.20) 0%, rgba(71, 133, 254, 0.20) 100%), #252529',
      }"
      @click="trackBannerClick">
      <div
        v-if="currentTheme === 'bf'"
        class="flex items-center gap-2 md:w-auto md:gap-5 xl:gap-20">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <img
            :src="
              phase?.icon
                ? `/images/banner-${phase?.icon}.svg`
                : '/images/banner-winter.svg'
            "
            class="hidden h-4 w-auto md:h-6"
            alt="logo">
          <div
            class=" hidden !text-center text-[12px] font-black  !leading-none text-white md:inline md:w-[120px]  md:text-base xl:w-auto [&>span]:text-brilliant-rose"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-5 xl:gap-20">
          <div class="flex w-auto flex-col items-center gap-0.5 md:w-auto xl:flex-row xl:gap-1">
            <div
              v-if="phase?.prelude"
              class="text-center text-[11px] font-black leading-none text-white md:hidden [&>span]:text-brilliant-rose"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.title"
              class="text-center text-[11px] font-normal !leading-none text-white md:whitespace-nowrap md:text-base xl:text-lg [&>b]:font-bold [&>span]:font-bold [&>span]:text-still-yellow"
              v-html="phase?.title" />
            <div
              v-if="phase?.subtitle"
              class="text-center text-[8px] font-normal !leading-none text-white md:text-sm xl:inline xl:text-base"
              v-html="phase?.subtitle" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-0 md:px-2 md:py-1 bg-transparent md:bg-brilliant-rose/20" />
          </div>

          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'conf'"
        class="flex items-center gap-5 md:w-auto xl:gap-10">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <InlineSvg
            src="/images/icon-fire.svg"
            class="h-4 w-auto text-brilliant-rose md:h-5"
            alt="fire" />
          <div
            class=" w-12 bg-gradient-to-r from-brilliant-rose to-dodger-blue bg-clip-text !text-center text-[13px] font-bold leading-none text-transparent md:block md:w-auto md:text-lg"
            v-html="phase?.prelude" />
        </div>

        <div class="mr-5 flex items-center gap-3 sm:mr-0 md:gap-4 xl:gap-8">
          <div class="flex w-[180px] shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:shrink md:flex-nowrap md:gap-x-8 lg:flex-row">
            <div
              class=" hidden !text-center text-[8px] font-bold leading-none text-white md:w-auto md:text-base"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.header"
              class="text-center text-[8px] font-bold text-white md:text-base"
              v-html="phase?.header" />
            <div
              v-if="phase?.title"
              class="text-center text-[9px] font-normal text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:font-bold"
              v-html="phase?.title" />
            <NuxtImg
              src="/images/Evan-You.png"
              class="ml-2 hidden size-[36px] rounded-full" />
            <div
              v-if="phase?.subtitle"
              class="hidden text-center text-[8px] font-normal text-white md:text-xs xl:inline xl:text-base"
              v-html="phase?.subtitle" />

            <div
              v-if="phase?.interlude"
              class="block text-center text-[10px] font-bold text-white md:text-base lg:hidden xl:text-base [&>span]:bg-gradient-to-r [&>span]:from-brilliant-rose [&>span]:to-dodger-blue [&>span]:bg-clip-text [&>span]:font-bold [&>span]:text-transparent"
              v-html="phase?.interlude" />
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-bold text-white md:text-base lg:block xl:ml-2 xl:text-base [&>span]:font-bold [&>span]:text-transparent xl:[&>span]:bg-brilliant-rose/20 xl:[&>span]:bg-gradient-to-r xl:[&>span]:from-brilliant-rose xl:[&>span]:to-dodger-blue xl:[&>span]:bg-clip-text xl:[&>span]:text-transparent"
            v-html="phase?.interlude" />

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-1 md:px-2 md:py-1"
              :box-style="
                phase?.darkMode
                  ? 'background: #176367'
                  : 'background: linear-gradient(107deg, rgba(248, 100, 189, 0.50) 0%, rgba(71, 133, 254, 0.50) 100%);'
              " />
          </div>

          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 1"
        class="flex items-center gap-5 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <img
            src="/images/banner-winter.svg"
            class="hidden h-4 w-auto md:block md:h-5"
            alt="logo">
          <div
            class=" w-12 !text-center text-[10px] font-bold leading-none  md:block md:w-auto md:text-base"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-14">
          <div class="flex max-w-[230px] shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
            <div
              v-if="phase?.prelude"
              class="!text-center text-[10px] md:hidden"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.header"
              class="text-center text-[10px] font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem]"
              v-html="phase?.header" />
            <div
              v-if="phase?.subtitle"
              class="block text-center text-[10px] font-normal text-white md:text-base xl:inline xl:text-base"
              v-html="phase?.subtitle" />
            <div
              v-if="phase?.interlude"
              class="block text-center text-[10px] font-normal text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
              v-html="phase?.interlude" />
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
            v-html="phase?.interlude" />

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap bg-gradient-to-r from-brilliant-rose to-dodger-blue bg-clip-text text-[8px]  font-bold uppercase text-transparent md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
              ends in
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-brilliant-rose/20"
              box-style="" />
          </div>

          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'free-weekend'"
        class="flex items-center gap-5 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-4">
          <img
            src="/images/banner-winter.svg"
            class="hidden h-4 w-auto md:h-5"
            alt="logo">
          <div
            class=" w-12 !text-left text-[10px] font-black leading-none text-white  md:block md:w-auto md:text-lg xl:text-2xl md:[&>.live]:pl-4 xl:[&>.live]:pl-8 [&>span]:bg-gradient-to-r [&>span]:from-brilliant-rose [&>span]:to-dodger-blue [&>span]:bg-clip-text [&>span]:text-transparent"
            v-html="phase?.prelude" />
        </div>

        <div class="flex flex-col items-center sm:flex-row sm:gap-3 md:gap-4 xl:gap-8 2xl:gap-14">
          <div class="flex shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 gap-y-0 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-col">
            <div
              v-if="phase?.prelude"
              class="!text-center text-[10px] text-white md:hidden [&>span]:bg-gradient-to-r [&>span]:from-brilliant-rose [&>span]:to-dodger-blue [&>span]:bg-clip-text [&>span]:text-transparent"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.header"
              class="text-center text-[10px] font-semibold leading-none text-white sm:max-w-[220px] md:text-sm md:!leading-[1.2rem] xl:max-w-none xl:whitespace-nowrap xl:text-base xl:leading-[1.2rem] [&>span]:bg-gradient-to-r [&>span]:from-brilliant-rose [&>span]:to-dodger-blue [&>span]:bg-clip-text [&>span]:text-transparent"
              v-html="phase?.header" />
            <div
              v-if="phase?.subtitle"
              class="block text-center text-[10px] font-normal text-white md:text-sm lg:text-base xl:inline xl:text-base [&>span]:bg-gradient-to-r [&>span]:from-brilliant-rose [&>span]:to-dodger-blue [&>span]:bg-clip-text [&>span]:text-transparent"
              v-html="phase?.subtitle" />
            <div
              v-if="phase?.addendum"
              class="block text-center text-[10px] font-normal text-white md:text-sm lg:hidden lg:text-base xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
              v-html="phase?.addendum" />
          </div>
          <div
            v-if="phase?.addendum"
            class="hidden text-center text-[10px] font-normal text-white md:text-base lg:block xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
            v-html="phase?.addendum" />

          <div
            v-if="phase?.showCountdown"
            class="flex flex-row items-center sm:flex-col lg:flex-row lg:items-center lg:gap-2">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap bg-gradient-to-r from-brilliant-rose to-dodger-blue bg-clip-text text-[8px]  font-bold uppercase text-transparent md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white lg:tracking-widest">
              {{ phase?.countdownFooter || 'ends in' }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-brilliant-rose p-1 md:px-2 md:py-1 bg-transparent md:bg-brilliant-rose/20"
              box-style="" />
          </div>
          <div
            v-show="phase?.cta && !route.path.includes('/vuejs')"
            class="hidden h-[30px] items-center justify-center gap-2 whitespace-nowrap rounded-md bg-brilliant-rose px-4 text-xs font-bold text-eerie-black  transition-all ease-linear group-hover:bg-brilliant-rose/80 lg:inline-flex">
            <span>{{ phase.cta }}</span>
            <span><svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'autumn-sales'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl -translate-x-3 items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/autumn-leaf.svg"
              class="size-6 text-brilliant-rose md:size-8"
              alt="Autumn Sales Banner" />
            <p
              class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-brilliant-rose"
              v-html="phase?.prelude" />
          </div>
          <div class="[&>p]:!leading-1 flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
            <p
              class="text-center text-sm font-normal text-white md:text-lg"
              v-html="phase?.title" />

            <p
              v-if="phase?.interlude"
              class="text-xs font-bold uppercase tracking-[1.8px] text-brilliant-rose md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXTENDED')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-brilliant-rose md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-brilliant-rose md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-brilliant-rose/20"
              box-style="" />
          </div>
        </div>
      </div>

      <div
        v-if="currentTheme === 'halloween-sale'"
        class="my-auto w-full">
        <div class="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
          <div class="flex items-center gap-2">
            <InlineSvg
              src="/images/pumpkin.svg"
              class="hidden size-6 sm:block  md:size-6"
              alt="Halloween Sales Banner" />
            <p
              class="w-24 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-[#FF6666]"
              v-html="phase?.prelude" />
          </div>
          <div class="flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20 [&>p]:!leading-[90%]">
            <p
              class="text-center text-sm font-normal text-white md:text-lg"
              v-html="phase?.title" />

            <p
              v-if="phase?.interlude"
              class="text-xs font-bold uppercase tracking-[1.8px] text-brilliant-rose md:text-lg"
              v-html="phase?.interlude" />
          </div>

          <div
            v-if="phase?.showCountdown"
            class="mt-1 flex flex-col items-center md:mt-0 md:flex-row md:gap-2">
            <span
              v-if="phase?.id?.includes('EXTENDED')"
              class="whitespace-nowrap text-[8px]  font-bold uppercase text-brilliant-rose md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="-mb-1 whitespace-nowrap text-[8px] font-bold uppercase text-white sm:mb-0 md:text-[10px] md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-[#FF6666] md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-[#FF6666]/20"
              box-style="" />
          </div>
        </div>
      </div>
    </NuxtLink>
    <button
      class="absolute inset-y-0 right-2 z-20 my-auto size-5 shrink-0 hover:bg-white/20 xl:right-3"
      @click="closeBanner">
      <span class="sr-only">close</span><XMarkIcon class="x-5 h-5 text-white" />
    </button>
  </div>
</template>
